import React, { useEffect, useState, useContext } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { firestore, functions } from "../shared/FirebaseConfig";
import { UserContext } from "../shared/UserContext";

const Invitations = ({ user: userProp, hideWhenEmpty }) => {
  const { user: userCtx } = useContext(UserContext);
  const user = userProp || userCtx;
  const [invitations, setInvitations] = useState([]);

  const getClassDetails = async (classId) => {
    try {
      const classDoc = await getDoc(doc(firestore, "classes", classId));
      if (classDoc.exists()) {
        return classDoc.data().className;
      }
    } catch (error) {
      console.error("Error fetching class details:", error);
    }
    return "Unknown Class"; // Default value if class is not found
  };

  // Inside your Invitations component:
  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        // Ensure user object exists before accessing its properties
        if (!user || !user.email) {
          return; // Exit early if user is null or email is not available
        }

        const q = query(
          collection(firestore, "invitations"),
          where("invitedToEmail", "==", user.email),
          where("status", "==", "pending")
        );

        const querySnapshot = await getDocs(q);
        const invitationData = [];

        for (const doc of querySnapshot.docs) {
          const invitation = doc.data();
          // Prefer the class name denormalized onto the invitation (readable even
          // when the invitee can't read the class doc); fall back to a lookup for
          // older invitations that predate the denormalization.
          const className = invitation.className || (await getClassDetails(invitation.classId));
          invitation.className = className;
          invitationData.push({ id: doc.id, ...invitation });
        }

        setInvitations(invitationData);
      } catch (error) {
        console.error("Error fetching invitations:", error);
      }
    };

    fetchInvitations();
  }, [user]);

  // Accepting a co-teacher invite adds the invitee to the class's coTeachers —
  // which Firestore rules can't allow (the invitee doesn't own the class and may
  // not even be same-school). A callable Cloud Function does the validated writes
  // server-side; the client just invokes it. Same for decline (rules would block
  // the invitee's invitation update too).
  const respond = async (invitationId, accept) => {
    try {
      const respondToInvitation = httpsCallable(functions, "respondToInvitation");
      await respondToInvitation({ invitationId, accept });
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    } catch (error) {
      console.error(`Error ${accept ? "accepting" : "declining"} invitation:`, error);
      window.alert(
        error?.message ||
          `Could not ${accept ? "accept" : "decline"} the invitation. Please try again.`
      );
    }
  };

  const handleAcceptInvitation = (invitationId) => respond(invitationId, true);
  const handleDeclineInvitation = (invitationId) => respond(invitationId, false);

  // Nothing pending: on the dashboard, hide entirely; on its own page, show the message.
  if (invitations.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <div className="card" style={{ marginBottom: "var(--space-5)" }}>
        <h2 style={{ marginBottom: "var(--space-2)" }}>Invitations</h2>
        <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
          You currently have no invitations.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: "var(--space-5)" }}>
      <h2 style={{ marginBottom: "var(--space-3)" }}>Pending Invitations</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {invitations.map((invitation) => (
          <div
            key={invitation.id}
            style={{
              display: "flex", flexWrap: "wrap", alignItems: "center",
              justifyContent: "space-between", gap: "var(--space-3)",
            }}
          >
            <div style={{ fontSize: "var(--text-sm)" }}>
              <strong>{invitation.className}</strong>
              <span style={{ color: "var(--color-text-muted)" }}>
                {" "}· {invitation.teacherRole} · from {invitation.invitedByEmail}
              </span>
            </div>
            <div style={{ display: "flex", gap: "var(--space-2)" }}>
              <button onClick={() => handleAcceptInvitation(invitation.id)}>
                Accept
              </button>
              <button
                className="btn-secondary"
                onClick={() => handleDeclineInvitation(invitation.id)}
              >
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Invitations;
